<template>
  <div class="forgot-password-modal">
    <div v-if="loadModal" class="modal modal-mask">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header div-responsive">
            <div class="modal-title">
              <h1>Verification Code</h1>
            </div>
            <button type="button" class="close-icon" @click="handleModal($event)" data-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body div-responsive">
            <div class="box-body">
              <div class="box-row">
                <div class="box-col">
                  <p style="line-height:1.5">A verification code has been sent to your registered email address. Please provide the code and your new password below.</p>
                </div>
              </div>
              <div class="box-row">
                <div class="box-col box-col-left">
                  Verification Code
                </div>
                <div class="box-col box-col-right">
                  <input type="text" :class="hasError && (!validateCode || !correctCode) ? 'is-invalid' : ''" v-model="code" placeholder="Verification Code" />
                  <div style="margin: 0" class="text-danger" v-if="hasError && (!validateCode || !correctCode)">
                    Please enter the verification code
                  </div>
                </div>
              </div>
              <div class="box-row">
                <div class="box-col box-col-left">
                  New password
                </div>
                <div class="box-col box-col-right">
                  <input type="password" :class="hasError && !validatePassword ? 'is-invalid' : ''" v-model="password" placeholder="New Password" />
                  <div class="text-danger password-validation-errors" v-if="hasError && !validatePassword">
                    <div v-for="(msg, idx) in passwordValidationErrors" style="min-height: 0; line-height: 1" :key="idx">{{ msg }}</div>
                  </div>
                  <div v-if="password" class="mb-4 mt-n2 password-strength-wrap">
                    <div style="margin-top: 5px" class="password-strength-meta">
                      <span>Password strength</span>
                      <span :class="newPasswordStrengthClass">{{ newPasswordStrength }}</span>
                    </div>
                    <v-progress-linear
                        :value="newPasswordStrengthPercent"
                        :color="newPasswordStrengthColor"
                        background-color="#eceff1"
                        height="8"
                        rounded
                    ></v-progress-linear>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col">
                  <div class="icon-group right-float-element">
                    <v-btn class="white-button" @click="handleModal($event)" dark>Cancel</v-btn>
                    <v-btn @click="clickSave" dark>Save</v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import utilities from '@/assets/js/utilities'
  import auth from '@/assets/js/cognitoAuth'
  import { getPasswordPolicyErrors } from '@/assets/js/passwordPolicy'

  export default {
    name: 'ForgotPasswordModal',
    props: [],
    data() {
      return {
        loadModal: false,
        user: {},
        hasError: false,
        code: '',
        password: '',
        correctCode: true,
      }
    },

    computed: {
      newPasswordStrengthLevel() {
        const password = this.password || ''
        if (!password) {
          return 0
        }
        if (this.passwordValidationErrors.length > 0) {
          return 1
        }
        const hasRepeatedChars = /(.)\1{2,}/.test(password)
        const hasSequentialPattern = this.hasSequentialPattern(password)
        const hasKeyboardPattern = this.hasKeyboardPattern(password)
        const normalized = this.normalizeForCommonWordCheck(password)
        const hasCommonWordPattern = this.hasCommonWordPattern(normalized)
        const noObviousPattern =
          !hasRepeatedChars &&
          !hasSequentialPattern &&
          !hasKeyboardPattern &&
          !hasCommonWordPattern
        const uniqueCharRatio =
          new Set(password.toLowerCase().split('')).size / password.length

        if (noObviousPattern && uniqueCharRatio >= 0.65) {
          return 4
        }
        if (noObviousPattern) {
          return 3
        }
        return 2
      },
      newPasswordStrength() {
        const level = this.newPasswordStrengthLevel
        if (level <= 1) return 'Weak'
        if (level === 2) return 'Medium'
        if (level === 3) return 'Strong'
        return 'Very Strong'
      },
      newPasswordStrengthClass() {
        if (this.newPasswordStrengthLevel === 4) {
          return 'strength-very-strong'
        }
        if (this.newPasswordStrengthLevel === 3) {
          return 'strength-strong'
        }
        if (this.newPasswordStrengthLevel === 2) {
          return 'strength-medium'
        }
        return 'strength-weak'
      },
      newPasswordStrengthPercent() {
        if (!this.password) {
          return 0
        }
        return this.newPasswordStrengthLevel * 25
      },
      newPasswordStrengthColor() {
        if (this.newPasswordStrengthLevel === 4) {
          return '#0b8043'
        }
        if (this.newPasswordStrengthLevel === 3) {
          return '#1e8e3e'
        }
        if (this.newPasswordStrengthLevel === 2) {
          return '#f29900'
        }
        return '#d93025'
      },
      validateCode() {
        return this.code !== ''
      },
      passwordValidationErrors() {
        return getPasswordPolicyErrors(this.password)
      },
      validatePassword() {
        return this.passwordValidationErrors.length === 0
      },
    },

    methods: {
      normalizeForCommonWordCheck(value) {
        const substitutions = {
          '0': 'o',
          '1': 'i',
          '3': 'e',
          '4': 'a',
          '5': 's',
          '7': 't',
          '@': 'a',
          '$': 's',
          '!': 'i',
        }
        return (value || '')
          .toLowerCase()
          .split('')
          .map((c) => substitutions[c] || c)
          .join('')
      },
      hasCommonWordPattern(normalizedPassword) {
        const commonWords = [
          'password',
          'welcome',
          'admin',
          'qwerty',
          'abc',
          'iloveyou',
          'letmein',
          'moonlight',
          'sunshine',
          'correcthorse',
          'battery',
        ]
        return commonWords.some((word) => normalizedPassword.includes(word))
      },
      hasKeyboardPattern(password) {
        const lower = (password || '').toLowerCase()
        const patterns = ['qwerty', 'asdfgh', 'zxcvbn']
        return patterns.some((pattern) => lower.includes(pattern))
      },
      hasSequentialPattern(password) {
        if (!password || password.length < 3) return false
        const chars = password.toLowerCase()
        let asc = 1
        let desc = 1
        for (let i = 1; i < chars.length; i += 1) {
          const diff = chars.charCodeAt(i) - chars.charCodeAt(i - 1)
          asc = diff === 1 ? asc + 1 : 1
          desc = diff === -1 ? desc + 1 : 1
          if (asc >= 3 || desc >= 3) {
            return true
          }
        }
        return false
      },
      handleModal(e) {
        this.loadModal = !this.loadModal
        if (this.loadModal) {
          $('html').addClass('modal-mask-open')
        } else {
          $('html').removeClass('modal-mask-open')
        }
      },
      async clickSave() {
        this.hasError = false
        if (!this.validatePassword) {
          this.hasError = true
          return
        }
        await this.$store.dispatch('loading/setLoadComponent', true)
        const pwdSHA = await utilities.logSha1(this.password)
        const res1 = await fetch('https://api.pwnedpasswords.com/range/' + pwdSHA.substring(0, 5))

        const res = await res1.text()
        const formatRes = res.split('\r\n')
        const item = formatRes.find(item => item.indexOf(pwdSHA.substring(5)) > -1)
        if(item) {
          this.$notify({
            dangerouslyUseHTMLString: true,
            message: '<div class="alert-warning">' +
                '            <b>Warning:</b> Your new password has been seen ' + item.split(':')[1] + ' times before, please change it' +
                '          </div>',
          });
          await this.$store.dispatch('loading/setLoadComponent', false)
          return
        }
        utilities.confirmNewPassword(this.code, this.password, this.$router.currentRoute.query.Session).then((res) => {
            if(res) {
                this.$parent.successMessage = "Your password has been reset successfully!"
                this.$parent.success()
                this.handleModal()
            } else {
                this.correctCode = false
                this.hasError = true
            }
        }).finally(() => {
          this.$store.dispatch('loading/setLoadComponent', false)
        })
      },
    },

    async beforeMount() {},

    mounted() {},
  }
</script>

<style>
.password-strength-wrap {
  margin-bottom: 12px;
}
::v-deep .v-text-field__details {
  min-height: 0;
}

::v-deep .v-text-field__details .v-messages{
  min-height: 0;
}


.password-strength-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  line-height: 20px;
}

.password-validation-errors {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.45;
}
.password-validation-errors ul {
  margin: 0;
  padding-left: 1.25rem;
}

.strength-weak {
  color: #d93025;
}

.strength-medium {
  color: #f29900;
}

.strength-strong {
  color: #1e8e3e;
}

.strength-very-strong {
  color: #0b8043;
  font-weight: 600;
}
</style>
